/**
 * Toast notifications.
 *
 * Replaces `window.alert()`, which the repo's own lint rule bans and which had
 * 32 call sites across 11 files. `alert()` is a genuine accessibility defect,
 * not just an aesthetic one:
 *   - it blocks the entire page and the JS thread until dismissed;
 *   - it renders in the browser chrome, so it ignores the app's theme entirely;
 *   - it cannot be styled, positioned, or made to match the product;
 *   - on a platform whose district-sale path runs through a WCAG AA audit, a
 *     modal a screen-reader user cannot navigate away from is a finding.
 *
 * Accessibility notes, since that is the whole point:
 *   - the live region is always mounted (an `aria-live` region added to the DOM
 *     at the same moment as its content is unreliably announced);
 *   - success/info use `role="status"` + `aria-live="polite"` so they wait for
 *     a pause in speech; errors use `role="alert"` + `aria-live="assertive"`
 *     because a failed save should interrupt;
 *   - auto-dismiss pauses on hover AND on keyboard focus, so a reader can
 *     finish before it disappears;
 *   - errors do not auto-dismiss at all — you should not lose a failure message
 *     because you looked away;
 *   - `prefers-reduced-motion` removes the slide-in.
 *
 * Dependency-free by choice: `sonner` and friends would be a new runtime
 * dependency for ~120 lines of behaviour.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Toast.css';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  /** Show a toast. Returns its id so a caller can dismiss it early. */
  notify: (message: string, kind?: ToastKind) => string;
  success: (message: string) => string;
  error: (message: string) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Success and info clear themselves; errors stay until dismissed. */
const AUTO_DISMISS_MS = 5000;

let counter = 0;
const nextId = () => `toast-${Date.now()}-${(counter += 1)}`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Paused while the pointer or keyboard focus is inside the stack.
  const paused = useRef(false);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const schedule = useCallback(
    (id: string) => {
      const timer = setTimeout(() => {
        // Re-check on fire rather than cancelling up front, so a toast that
        // arrives while the user is hovering still expires afterwards.
        if (paused.current) {
          schedule(id);
          return;
        }
        dismiss(id);
      }, AUTO_DISMISS_MS);
      timers.current.set(id, timer);
    },
    [dismiss]
  );

  const notify = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = nextId();
      setToasts((prev) => {
        // Cap the stack so a loop of failures cannot fill the screen.
        const next = [...prev, { id, kind, message }];
        return next.length > 4 ? next.slice(next.length - 4) : next;
      });
      if (kind !== 'error') schedule(id);
      return id;
    },
    [schedule]
  );

  const success = useCallback((m: string) => notify(m, 'success'), [notify]);
  const error = useCallback((m: string) => notify(m, 'error'), [notify]);

  // Clear every pending timer on unmount.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => clearTimeout(t));
      pending.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ notify, success, error, dismiss }),
    [notify, success, error, dismiss]
  );

  const polite = toasts.filter((t) => t.kind !== 'error');
  const assertive = toasts.filter((t) => t.kind === 'error');

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="toast-stack"
        onMouseEnter={() => {
          paused.current = true;
        }}
        onMouseLeave={() => {
          paused.current = false;
        }}
        onFocusCapture={() => {
          paused.current = true;
        }}
        onBlurCapture={() => {
          paused.current = false;
        }}
      >
        {/* Both regions stay mounted so announcements are reliable. */}
        <div role="status" aria-live="polite" aria-atomic="false">
          {polite.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>
        <div role="alert" aria-live="assertive" aria-atomic="false">
          {assertive.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = 'ToastProvider';

const ICONS: Record<ToastKind, string> = {
  success: '✓',
  error: '!',
  info: 'i',
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = React.memo(
  ({ toast, onDismiss }) => (
    <div className={`toast toast-${toast.kind}`}>
      <span className="toast-icon" aria-hidden="true">
        {ICONS[toast.kind]}
      </span>
      <span className="toast-message">{toast.message}</span>
      <button
        type="button"
        className="toast-close"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  )
);

ToastItem.displayName = 'ToastItem';

/**
 * Show notifications from a component.
 *
 * Falls back to a no-op-with-warning rather than throwing when no provider is
 * mounted: a missing provider should not crash a page over a "copied to
 * clipboard" message. The warning makes the mistake findable.
 */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;

  return {
    notify: (message: string) => {
      console.warn('useToast called outside ToastProvider; dropped:', message);
      return '';
    },
    success: (message: string) => {
      console.warn('useToast called outside ToastProvider; dropped:', message);
      return '';
    },
    error: (message: string) => {
      console.warn('useToast called outside ToastProvider; dropped:', message);
      return '';
    },
    dismiss: () => undefined,
  };
};
